import React, { useEffect, useMemo, useState } from 'react';
import { fetchBillingPlans, fetchAdminPlanByUuid, updatePlanByUuid } from '../../api/adminClient';
import { formatCurrency } from '../../utils/formatters';

const toIntegerString = (value) => {
  if (value == null || value === '') {
    return '';
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : '';
};

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlanUuid, setSelectedPlanUuid] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({
    plan_name: '',
    plan_details: '',
    plan_price: '',
    hours_granted: '',
    consumable_plan_price: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const cautionMessage = useMemo(
    () =>
      'Caution: Updated price or hours will apply to users who sign up after this update. Existing customers will be updated in the following months.',
    []
  );

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await fetchBillingPlans();
      setPlans(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleOpenPlan = async (planUuid) => {
    setLoading(true);
    try {
      const response = await fetchAdminPlanByUuid(planUuid);
      const plan = response.data?.data || null;
      setSelectedPlanUuid(planUuid);
      setSelectedPlan(plan);
      setForm({
        plan_name: plan?.plan_name || '',
        plan_details: plan?.plan_details || '',
        plan_price: plan?.plan_price ?? '',
        hours_granted: toIntegerString(plan?.hours_granted),
        consumable_plan_price:
          plan?.consumable?.plan_price ?? plan?.consumablePlans?.[0]?.plan_price ?? '',
      });
    } catch (error) {
      console.error('Failed to fetch plan details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedPlanUuid(null);
    setSelectedPlan(null);
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlanUuid) {
      return;
    }

    const body = {};
    if (form.plan_name.trim() !== '') body.plan_name = form.plan_name.trim();
    if (form.plan_details.trim() !== '') body.plan_details = form.plan_details.trim();
    if (form.plan_price !== '') body.plan_price = Number(form.plan_price);
    if (form.hours_granted !== '') body.hours_granted = Number(form.hours_granted);
    if (form.consumable_plan_price !== '') {
      body.consumable = { plan_price: Number(form.consumable_plan_price) };
    }

    setSaving(true);
    try {
      await updatePlanByUuid(selectedPlanUuid, body);
      await handleOpenPlan(selectedPlanUuid);
      await loadPlans();
    } catch (error) {
      console.error('Failed to update plan:', error);
    } finally {
      setSaving(false);
    }
  };

  if (selectedPlanUuid) {
    return (
      <div className="plan-details-view">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            type="button"
            className="btn btn-outline-secondary rounded-pill"
            onClick={handleBackToList}
          >
            ← Back to Plans
          </button>
          <button
            type="button"
            className="btn btn-primary rounded-pill"
            onClick={handleUpdatePlan}
            disabled={loading || saving}
          >
            {saving ? 'Updating...' : 'Update'}
          </button>
        </div>

        <div className="alert alert-warning mb-4" role="alert">
          {cautionMessage}
        </div>

        <div className="card-soft">
          <div className="p-4">
            <h1 className="display-6 fw-semibold mb-4">{selectedPlan?.plan_name || 'Plan Details'}</h1>
            <div className="row g-3">
              <div className="col-12">
                <label htmlFor="plan_name" className="form-label fw-semibold">Plan Name</label>
                <input
                  id="plan_name"
                  className="form-control"
                  value={form.plan_name}
                  onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label htmlFor="plan_details" className="form-label fw-semibold">Plan Details</label>
                <textarea
                  id="plan_details"
                  className="form-control"
                  rows="3"
                  value={form.plan_details}
                  onChange={(e) => setForm({ ...form, plan_details: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="plan_price" className="form-label fw-semibold">Plan Price</label>
                <input
                  id="plan_price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={form.plan_price}
                  onChange={(e) => setForm({ ...form, plan_price: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="hours_granted" className="form-label fw-semibold">Hours Granted</label>
                <input
                  id="hours_granted"
                  type="number"
                  min="0"
                  step="1"
                  className="form-control"
                  value={form.hours_granted}
                  onChange={(e) => setForm({ ...form, hours_granted: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="consumable_plan_price" className="form-label fw-semibold">
                  Add-on price for additional hour
                </label>
                <input
                  id="consumable_plan_price"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={form.consumable_plan_price}
                  onChange={(e) => setForm({ ...form, consumable_plan_price: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="plans-view">
      <div className="d-flex flex-column flex-lg-row gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-semibold mb-1">Plan Management</h1>
          <p className="text-muted mb-0">Manage subscription plans and their monthly granted hours.</p>
        </div>
      </div>

      <div className="row g-4">
        {plans.length > 0 ? (
          plans.map((plan) => (
            <div key={plan.uuid} className="col-12 col-lg-6">
              <button
                type="button"
                className="card-soft h-100 w-100 text-start border-0"
                onClick={() => handleOpenPlan(plan.uuid)}
                disabled={loading}
              >
                <h5 className="fw-semibold mb-2">{plan.plan_name || '—'}</h5>
                <p className="text-muted mb-2">{plan.plan_details || '—'}</p>
                <div className="d-flex flex-column gap-1">
                  <small className="text-muted">Hours Granted: {plan.hoursGranted ?? '—'}</small>
                  <small className="text-muted">Plan Price: {formatCurrency(plan.plan_price)}</small>
                </div>
              </button>
            </div>
          ))
        ) : (
          <div className="col-12">
            <div className="card-soft text-center py-5">
              <h5 className="text-muted mb-3">{loading ? 'Loading plans...' : 'No plans found'}</h5>
              <p className="text-muted mb-0">Plans are currently unavailable.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanManagement;
